import { Injectable, inject } from "@angular/core";
import { Auth } from "aws-amplify";
import { dateUtil } from "src/app/support/date-util";
import { find } from "lodash-es";
import { UserBackend } from "src/app/backend/api/user.backend";
import { BootstrapService } from "../modules/bootstrap/bootstrap.service";
import { jwtDecodeExp } from "../support/jwt";
import { sntLog } from "src/app/common/logger";
import { isAuthenticated, logout } from "src/app/common/auth";
import { SessionService } from "src/app/services/session.service";
import { jwtDecode } from "jwt-decode";
import { lastValueFrom } from "rxjs";
import { Router } from "@angular/router";
import { maintenance } from "src/app/support/maintenance";
import { ErrorCodes } from "src/app/modules/maintenance/error-codes";
import { Mutex } from "async-mutex";
import { environment } from "src/environments/environment";

const logger = sntLog("AuthService");

export type AuthFeatures = {
  enableSignup?: boolean;
};

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly sessionService = inject(SessionService);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly userBackend = inject(UserBackend);
  private readonly router = inject(Router);

  private readonly mutexAuthorizationBearerToken = new Mutex();

  async isAuthenticated(): Promise<boolean> {
    return await isAuthenticated();
  }

  async signOut(): Promise<void> {
    await logout(this.bootstrapService.getCurrentMember(), this.sessionService);
  }

  async getCurrentAuthenticatedUserCached(): Promise<any> {
    return await Auth.currentAuthenticatedUser({
      bypassCache: false
    });
  }

  async getAuthorizationBearerToken(): Promise<string | null> {
    try {
      await this.mutexAuthorizationBearerToken.acquire();

      const currentSession = await Auth.currentSession();

      return `Bearer ${currentSession.getIdToken().getJwtToken()}`;
    } catch (e) {
      return null;
    } finally {
      this.mutexAuthorizationBearerToken.release();
    }
  }

  async getMemberBearerToken(noRefresh: boolean): Promise<string | null> {
    const currentMemberAuth = this.bootstrapService.getCurrentMemberAuth();
    if (!currentMemberAuth) {
      return null;
    }

    return await this.getValidBearerToken(
      currentMemberAuth,
      undefined,
      noRefresh
    );
  }

  async getValidBearerToken(
    member: { token: string; id: string; account: string },
    tenantType: string | undefined,
    noRefresh: boolean
  ): Promise<string> {
    let token = member.token;

    const expire = jwtDecodeExp(token);

    const isExpired = dateUtil(expire * 1000).isBefore(
      dateUtil().add(1, "minutes")
    );

    if (!noRefresh && isExpired) {
      logger.warn(`member token expired`);
      token = await this.refreshMemberToken(member, tenantType);
    }

    const decodedJwt = jwtDecode<{ disabled: boolean }>(token);
    if (decodedJwt.disabled) {
      failure(this.router);
    }

    return `Bearer ${token}`;
  }

  private async refreshMemberToken(
    currentMemberAuth: {
      id: string;
      account: string;
    },
    tenantType: string | undefined
  ): Promise<string> {
    const members = await lastValueFrom(
      this.userBackend.refreshMembers(currentMemberAuth.account)
    );

    const refreshedMember = find(
      members,
      (x) => x.id === currentMemberAuth.id
    )!;

    /*
      Questo controllo serve ad evitare l'override del memberToken corrente
      se il refresh è il refresh del token di Rocket utilizzato nel PMS
     */
    if (
      !tenantType ||
      this.bootstrapService.getCurrentTenant()!.type === tenantType
    ) {
      this.bootstrapService.setCurrentMemberAuth(refreshedMember);
    }

    return refreshedMember.token;
  }
}

function failure(router: Router): boolean {
  maintenance(router, { code: ErrorCodes.ERR_007 });

  return false;
}
